import { useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { useViewport } from '@/hooks/useViewport';
import { formatDateWithPattern } from '@/utils/date-time/formatDateWithPattern';

const BAR_BORDER_RADIUS = 4;
const LEGEND_PADDING = 25;
const LEGEND_BOX_HEIGHT = 9;
const GRID_LINE_WIDTH = 1;
const BAR_WIDTH_BELOW_MD = 0.5;
const BAR_WIDTH_ABOVE_MD = 1.0;
const CATEGORY_WIDTH_BELOW_MD = 0.5;
const CATEGORY_WIDTH_ABOVE_MD = 0.2;

/**
 * @param {Array<{ date: string, malicious?: number }>} data
 * @returns {{ labels: string[], maliciousCodesData: number[] }}
 */
export const getChartConfig = (data) => {
  const labels = data.map(({ date }) => formatDateWithPattern(date, 'd MMM'));
  const maliciousCodesData = data.map(({ malicious }) => malicious || 0);
  return { labels, maliciousCodesData };
};

/**
 * @param {Object} theme - Tema (styled-components)
 * @param {{ width: number }} viewport
 * @returns {{ base: Object, maliciousColor: string }}
 */
export const getDatasetStyle = (theme, viewport) => {
  const mdBreakpoint = parseInt(theme.v3.breakpoints.md, 10);
  const isBelowMd = viewport.width < mdBreakpoint;
  const barWidth = isBelowMd ? BAR_WIDTH_BELOW_MD : BAR_WIDTH_ABOVE_MD;
  const categoryWidth = isBelowMd ? CATEGORY_WIDTH_BELOW_MD : CATEGORY_WIDTH_ABOVE_MD;
  return {
    base: {
      type: 'bar',
      borderRadius: BAR_BORDER_RADIUS,
      categoryPercentage: categoryWidth,
      barPercentage: barWidth,
    },
    maliciousColor: theme.v3.colors.brand.snappyBlue[80],
  };
};

/**
 * @param {Object} theme - Tema (styled-components)
 * @returns {Object} Opções do Chart.js
 */
export const getChartOptions = theme => ({
  backgroundColor: 'transparent',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        padding: LEGEND_PADDING,
        color: theme.v3.colors.neutral[40],
        font: {
          family: theme.v3.font.family.brand,
          size: theme.v3.font.sizes.small,
          weight: theme.v3.font.weights.medium,
        },
        usePointStyle: true,
        boxHeight: LEGEND_BOX_HEIGHT,
      },
    },
    tooltip: {
      bodyFont: {
        family: theme.v3.font.family.brand,
      },
      titleFont: {
        family: theme.v3.font.family.brand,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: theme.v3.colors.neutral[40],
        font: {
          family: theme.v3.font.family.brand,
          size: theme.v3.font.sizes.small,
          weight: theme.v3.font.weights.medium,
        },
      },
    },
    y: {
      stacked: false,
      grid: {
        display: true,
        color: theme.v3.colors.neutral[90],
        lineWidth: GRID_LINE_WIDTH,
      },
      ticks: {
        precision: 0,
        color: theme.v3.colors.neutral[40],
        font: {
          family: theme.v3.font.family.brand,
          size: theme.v3.font.sizes.small,
          weight: theme.v3.font.weights.medium,
        },
      },
    },
  },
});

/**
 * Agrega o retorno das funções auxiliares (getChartConfig, getDatasetStyle, getChartOptions)
 * e centraliza o uso de theme e viewport. Recebe os dados já paginados (página atual).
 * @param {Array<{ date: string, malicious?: number }>} data - Dados da página atual
 * @param {{ maliciousCodesLabel: string }} content
 * @returns {{ isEmpty: boolean, chartData: Object|null, chartOptions: Object|null }}
 */
export const useActivitiesChart = (data, content) => {
  const theme = useContext(ThemeContext);
  const viewport = useViewport();

  const isEmpty = !data || data.length === 0;

  const chartState = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: null, chartOptions: null };
    }
    const { labels, maliciousCodesData } = getChartConfig(data);
    const { base, maliciousColor } = getDatasetStyle(theme, viewport);
    const chartOptions = getChartOptions(theme);
    const chartData = {
      labels,
      datasets: [
        {
          ...base,
          label: content.maliciousCodesLabel,
          backgroundColor: maliciousColor,
          data: maliciousCodesData,
        },
      ],
    };
    return { chartData, chartOptions };
  }, [data, content, theme, viewport]);

  if (isEmpty) {
    return { isEmpty: true, chartData: null, chartOptions: null };
  }

  return {
    isEmpty: false,
    chartData: chartState.chartData,
    chartOptions: chartState.chartOptions,
  };
};
